/**
 * BindingEditorPage — admin Binding Editor (PIM Wave P1 Task 8, design notes
 * Surface 4). Route `/admin/categories/:id/fields` (registered in Task 11,
 * gated `marketplace.attribute.manage` like every other `/admin/catalog/*`
 * surface).
 *
 * Data assembly: the category itself, its ancestor chain, and every
 * ancestor's DIRECT bindings all come from the drained flat admin lists
 * (`useAllAdminCategories()`, `useAllAttributeFields()`) — there is no admin
 * category-tree endpoint (recon C4) NOR a resolved-bindings-with-provenance
 * endpoint (USER RULING C3). `ancestorIdsOf` below mirrors
 * `CategoryListPage.tsx`'s private helper of the same name (walks
 * `parent_id` up to the root, nearest first) — duplicated locally since
 * `CategoryListPage` doesn't export it.
 *
 * Layout: two-pane grid at `lg:` (≥1024px, design notes Surface 4) — left
 * column = inherited panel + direct-bindings table + add-field picker
 * trigger; right column = `FormPreview` (Task 10 — completes the mount
 * point Task 8 left as a comment), sticky within the grid. Below `lg:` the
 * grid collapses to a single column, gated by a mobile Editor/Preview
 * `Segmented` toggle (Task 10 — Task 8 deliberately deferred this: nothing
 * to switch to before the preview existed). `FormPreview` takes only
 * `categoryId` as a prop and fetches its own data from the PUBLIC
 * form-schema endpoint — it never reads any of this page's admin
 * bindings/fields state (zero-drift rule, Task 10 brief).
 *
 * Reorder: PUT-exhaustive `ordered_binding_ids` (recon: bindings have a
 * batch reorder route, unlike attribute-field options) — mirrors
 * `OptionsEditor.tsx`'s dnd-kit `handleDragEnd` shape (optimistic order,
 * rollback + toast + refetch on failure) but always sends the FULL id list,
 * never a changed-only subset (the backend requires exhaustiveness, not
 * mirroring the "only PATCH what changed" options idiom).
 *
 * Remove-binding confirm: ONE `BottomSheet` at this page level (mirrors
 * `CategoryListPage`/`FieldLibraryPage`'s single archive-confirm sheet),
 * not one per row — `BindingRow` only calls back up via `onRequestRemove`.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { BottomSheet, Button, Segmented } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminPageHeader, AdminStates } from "@/features/admin/_shared";
import { useAllAdminCategories } from "@/features/admin/categories/hooks";
import type { AdminCategory } from "@/features/admin/categories/api";
import { useAllAttributeFields } from "@/features/admin/attribute-fields/hooks";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import {
  useAncestorCategoryBindings,
  useCategoryBindings,
  useDeleteCategoryBinding,
  useReorderCategoryBindings,
} from "@/features/admin/category-bindings/hooks";
import type { CategoryBinding } from "@/features/admin/category-bindings/api";
import { BindingRow } from "@/features/admin/category-bindings/components/BindingRow";
import { InheritedBindingsPanel } from "@/features/admin/category-bindings/components/InheritedBindingsPanel";
import { AddFieldPicker } from "@/features/admin/category-bindings/components/AddFieldPicker";
import { FormPreview } from "@/features/admin/category-bindings/components/FormPreview";
import { PendingChangesBanner } from "@/features/admin/changesets/components/PendingChangesBanner";

const EMPTY_CATEGORIES: AdminCategory[] = [];
const EMPTY_FIELDS: AttributeField[] = [];
const EMPTY_BINDINGS: CategoryBinding[] = [];

/** Mirrors `CategoryListPage.tsx`'s private `categoryLabel` (current-admin-
 * language label with RU fallback, brief §3/design notes). */
function categoryLabel(node: AdminCategory, lang: string): string {
  if (lang === "ky" && node.name_ky) return node.name_ky;
  if (lang === "en" && node.name_en) return node.name_en;
  return node.name_ru;
}

/** Mirrors `CategoryListPage.tsx`'s private `ancestorIdsOf` (nearest-first
 * `parent_id` walk up to the root) — duplicated locally, not exported there.
 *
 * Guard (final-review Finding C): a soft-deleted middle ancestor is absent
 * from the drained flat admin list, so `byId.get(current.parent_id)` comes
 * back `undefined` for it. Only push an id once it's confirmed to resolve
 * in `byId` — an unresolvable id must never reach the returned list, since
 * this page threads `ancestorIds` straight into the ancestor-binding fan-out
 * (`useAncestorCategoryBindings`) and a 404 there wedges
 * `InheritedBindingsPanel` into its error state. Stop the walk at the first
 * unresolvable ancestor: everything farther up is unreachable by design
 * (same "nearest-first, stop at the first break" contract as before), so
 * the panel still renders the resolvable portion of the chain normally. */
function ancestorIdsOf(id: string, byId: Map<string, AdminCategory>): string[] {
  const ids: string[] = [];
  let current = byId.get(id);
  while (current && current.parent_id) {
    const parent = byId.get(current.parent_id);
    if (!parent) break;
    ids.push(current.parent_id);
    current = parent;
  }
  return ids;
}

interface OptimisticOrder {
  orderedIds: string[];
}

export function BindingEditorPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const lang = i18n.language;
  const { id: categoryId = "" } = useParams<{ id: string }>();

  // --- Category + ancestor chain (drained flat list — recon C4/USER RULING C3). ---
  const {
    data: allCategories,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useAllAdminCategories();
  const categories = allCategories ?? EMPTY_CATEGORIES;
  const categoriesById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const category = categoriesById.get(categoryId) ?? null;
  const notFound = !categoriesLoading && !categoriesError && category === null;

  const ancestorIds = useMemo(
    () => (category ? ancestorIdsOf(categoryId, categoriesById) : []),
    [category, categoryId, categoriesById],
  );
  const ancestors = useMemo(
    () =>
      ancestorIds
        .map((id) => categoriesById.get(id))
        .filter((c): c is AdminCategory => c !== undefined),
    [ancestorIds, categoriesById],
  );
  const breadcrumbChain = useMemo(() => [...ancestors].reverse(), [ancestors]);

  // --- Direct bindings (current category). ---
  const bindingsQuery = useCategoryBindings(category ? categoryId : null);
  const directBindings = useMemo(
    () => (bindingsQuery.data?.bindings ?? EMPTY_BINDINGS).slice().sort((a, b) => a.sort_order - b.sort_order),
    [bindingsQuery.data],
  );
  const directFieldIds = useMemo(() => new Set(directBindings.map((b) => b.field_id)), [directBindings]);
  const nextSortOrder = directBindings.length;

  // --- Ancestor bindings fan-out — ALSO consumed here (not just inside
  // `InheritedBindingsPanel`) to compute `inheritedFieldIds` for the
  // add-field picker. Same query keys as the panel's own fan-out, so
  // TanStack Query dedupes the network requests (shared cache entry, two
  // subscribers) — this is not a second round-trip. ---
  const ancestorBindingQueries = useAncestorCategoryBindings(ancestorIds);
  const inheritedFieldIds = useMemo(() => {
    const ids = new Set<string>();
    for (const q of ancestorBindingQueries) {
      for (const b of q.data?.bindings ?? []) ids.add(b.field_id);
    }
    return ids;
  }, [ancestorBindingQueries]);

  // --- Field catalogue (drained — additive `attribute-fields` helper). ---
  const fieldsQuery = useAllAttributeFields();
  const fields = fieldsQuery.data ?? EMPTY_FIELDS;
  const fieldsById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

  // --- Visibility-rule candidate fields (Task 9): select/boolean fields on
  // the category's EFFECTIVE chain — the union of direct + ancestor binding
  // field-ids (a field bound on two ancestors still counts once; nearest-
  // wins shadowing doesn't change WHETHER the field is on the chain, only
  // which ancestor's binding is authoritative). Computed ONCE here from data
  // already fetched above (no re-fetch, brief §2) and passed to every row's
  // `VisibilityRuleBuilder` via `BindingRow`; each row additionally excludes
  // its OWN field_id (a field can't control itself).
  //
  // TWO views over the same chain (post-review fix, Finding 2): archived
  // controlling fields must not be offered for NEW rule authoring, but a
  // PERSISTED rule referencing an archived-but-still-bound field must still
  // RESOLVE for display (backend fail-hides it, but that's a distinct
  // situation from a genuinely unbound/wrong-type field) — so the archived
  // filter applies ONLY to the NEW-pick list, never to the resolution
  // superset. ---
  const visibilityChainFields = useMemo(() => {
    const chainFieldIds = new Set<string>([...directFieldIds, ...inheritedFieldIds]);
    return Array.from(chainFieldIds)
      .map((id) => fieldsById.get(id))
      .filter(
        (f): f is AttributeField =>
          f !== undefined && (f.field_type === "select" || f.field_type === "boolean"),
      );
  }, [directFieldIds, inheritedFieldIds, fieldsById]);

  /** NEW-rule pick list — non-archived only. */
  const visibilityCandidateFields = useMemo(
    () =>
      visibilityChainFields
        .filter((f) => !f.is_archived)
        .slice()
        .sort((a, b) => a.name_ru.localeCompare(b.name_ru, "ru")),
    [visibilityChainFields],
  );

  /** Resolution superset (archived included) — DISPLAY of a persisted rule only. */
  const visibilityResolutionFields = useMemo(
    () => visibilityChainFields.slice().sort((a, b) => a.name_ru.localeCompare(b.name_ru, "ru")),
    [visibilityChainFields],
  );

  // --- Visibility-rule candidate readiness (post-review fix, Finding 1):
  // `fieldsQuery` (field drain) and `ancestorBindingQueries` (ancestor
  // binding fan-out) are NOT gated behind this page's `AdminStates` blocks
  // (only `categoriesLoading`/`bindingsQuery.isLoading` are — see JSX
  // below), so a row/sheet CAN mount before either finishes loading. Threaded
  // down so `VisibilityRuleBuilder` shows a neutral loading state instead of
  // ever misclassifying an in-flight resolution as an unresolvable rule. ---
  const visibilityCandidatesLoading =
    categoriesLoading ||
    bindingsQuery.isLoading ||
    fieldsQuery.isLoading ||
    ancestorBindingQueries.some((q) => q.isLoading);

  // --- Reorder (PUT-exhaustive). ---
  const reorderMutation = useReorderCategoryBindings();
  const [optimisticOrder, setOptimisticOrder] = useState<OptimisticOrder | null>(null);
  const [reorderPending, setReorderPending] = useState(false);

  const displayedBindings = useMemo(() => {
    if (!optimisticOrder) return directBindings;
    const byId = new Map(directBindings.map((b) => [b.id, b]));
    return optimisticOrder.orderedIds
      .map((id) => byId.get(id))
      .filter((b): b is CategoryBinding => b !== undefined);
  }, [optimisticOrder, directBindings]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = displayedBindings.findIndex((b) => b.id === active.id);
    const newIndex = displayedBindings.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(displayedBindings, oldIndex, newIndex);
    const orderedIds = reordered.map((b) => b.id);
    setOptimisticOrder({ orderedIds });
    setReorderPending(true);
    try {
      await reorderMutation.mutateAsync({ categoryId, body: { ordered_binding_ids: orderedIds } });
    } catch {
      toast.error(t("pim.bindings.table.reorderError"));
      await bindingsQuery.refetch();
    } finally {
      setOptimisticOrder(null);
      setReorderPending(false);
    }
  };

  // --- Remove (confirm at page level — see file header). ---
  const deleteMutation = useDeleteCategoryBinding();
  const [removeTarget, setRemoveTarget] = useState<CategoryBinding | null>(null);

  const confirmRemove = (): void => {
    if (!removeTarget) return;
    deleteMutation.mutate(
      { categoryId, bindingId: removeTarget.id },
      {
        onSuccess: () => {
          toast.success(t("pim.bindings.row.removeSuccess"));
          setRemoveTarget(null);
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  // --- Add-field picker. ---
  const [pickerOpen, setPickerOpen] = useState(false);

  const bindingsEmpty = !bindingsQuery.isLoading && !bindingsQuery.isError && directBindings.length === 0;

  // --- Mobile Editor/Preview toggle (Task 10 — completes the toggle Task 8
  // deferred). Only meaningful below `lg:`; at `lg:` both panes always show
  // side by side regardless of this state. ---
  const [mobileView, setMobileView] = useState<"editor" | "preview">("editor");

  return (
    <div data-testid="pim-binding-editor-page" className="flex flex-col gap-4">
      {category ? (
        <nav
          aria-label={t("pim.bindings.page.breadcrumbLabel")}
          data-testid="pim-binding-breadcrumb"
          className="flex flex-wrap items-center gap-1 text-sm text-[var(--g-secondary)]"
        >
          {breadcrumbChain.map((ancestor) => (
            <span key={ancestor.id} className="flex items-center gap-1">
              <Link
                to="/admin/categories"
                className="rounded-[var(--g-radius-sm)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                {categoryLabel(ancestor, lang)}
              </Link>
              <span aria-hidden="true">/</span>
            </span>
          ))}
          <span className="font-medium text-[var(--g-primary-ink)]">{categoryLabel(category, lang)}</span>
        </nav>
      ) : null}

      <AdminPageHeader
        title={category ? categoryLabel(category, lang) : t("pim.bindings.page.titleFallback")}
        subtitle={t("pim.bindings.page.subtitle")}
        actions={
          <Button
            variant="primary"
            size="md"
            leading="plus"
            data-testid="pim-bindings-add-field"
            onClick={() => setPickerOpen(true)}
            disabled={category === null}
            className="min-h-[44px]"
          >
            {t("pim.bindings.page.addField")}
          </Button>
        }
      />

      <PendingChangesBanner />

      <AdminStates
        isLoading={categoriesLoading}
        isError={categoriesError}
        isEmpty={notFound}
        loadingLabel={t("loading")}
        emptyLabel={t("pim.bindings.page.notFound")}
        errorLabel={t("error.loadFailed")}
        testid="pim-binding-editor"
      >
        <div
          role="group"
          aria-label={t("pim.preview.mobileToggle.label")}
          data-testid="pim-bindings-mobile-toggle"
          className="lg:hidden"
        >
          <Segmented
            options={[
              { value: "editor", label: t("pim.preview.mobileToggle.editor") },
              { value: "preview", label: t("pim.preview.mobileToggle.preview") },
            ]}
            value={mobileView}
            onChange={(next) => {
              if (next === "editor" || next === "preview") setMobileView(next);
            }}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <div
            className={[
              "flex-col gap-4",
              mobileView === "editor" ? "flex" : "hidden",
              "lg:flex",
            ].join(" ")}
          >
            <InheritedBindingsPanel
              categoryId={categoryId}
              ancestorIds={ancestorIds}
              ancestors={ancestors}
              fieldsById={fieldsById}
              directFieldIds={directFieldIds}
              nextSortOrder={nextSortOrder}
              lang={lang}
            />

            <h2 className="text-sm font-semibold text-[var(--g-primary-ink)]">
              {t("pim.bindings.table.title")}
            </h2>

            <AdminStates
              isLoading={bindingsQuery.isLoading}
              isError={bindingsQuery.isError}
              isEmpty={bindingsEmpty}
              loadingLabel={t("loading")}
              emptyLabel={t("pim.bindings.table.empty")}
              errorLabel={t("error.loadFailed")}
              testid="pim-bindings-table"
            >
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
                <SortableContext items={displayedBindings.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <ul className="flex flex-col gap-2" data-testid="pim-bindings-list">
                    {displayedBindings.map((binding) => (
                      <BindingRow
                        key={binding.id}
                        categoryId={categoryId}
                        binding={binding}
                        field={fieldsById.get(binding.field_id)}
                        dragDisabled={reorderPending}
                        onRequestRemove={setRemoveTarget}
                        candidateFields={visibilityCandidateFields}
                        resolutionFields={visibilityResolutionFields}
                        candidatesLoading={visibilityCandidatesLoading}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            </AdminStates>

            {bindingsEmpty ? (
              <div className="flex justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  leading="plus"
                  data-testid="pim-bindings-empty-add"
                  onClick={() => setPickerOpen(true)}
                  className="min-h-[44px]"
                >
                  {t("pim.bindings.table.emptyCta")}
                </Button>
              </div>
            ) : null}
          </div>

          <aside
            className={[
              mobileView === "preview" ? "block" : "hidden",
              "lg:block lg:sticky lg:top-4",
            ].join(" ")}
            data-testid="pim-bindings-preview-slot"
          >
            <FormPreview categoryId={categoryId} />
          </aside>
        </div>
      </AdminStates>

      <AddFieldPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        categoryId={categoryId}
        fields={fields}
        isLoading={fieldsQuery.isLoading}
        isError={fieldsQuery.isError}
        directFieldIds={directFieldIds}
        inheritedFieldIds={inheritedFieldIds}
        nextSortOrder={nextSortOrder}
      />

      <BottomSheet
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={t("pim.bindings.row.removeConfirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("pim.bindings.row.removeConfirmBody", {
              name: removeTarget ? (fieldsById.get(removeTarget.field_id)?.name_ru ?? removeTarget.field_id) : "",
            })}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRemoveTarget(null)}
              disabled={deleteMutation.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="pim-bindings-remove-confirm"
              loading={deleteMutation.isPending}
              onClick={confirmRemove}
              className="min-h-[44px]"
            >
              {t("pim.bindings.row.removeConfirm")}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

export default BindingEditorPage;
